import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Button, Nav, Alert, Spinner } from 'react-bootstrap';
import {
    Users,
    Monitor,
    TrendingUp,
    AlertTriangle,
    CheckCircle,
    Clock,
    UserCheck,
    Laptop,
    Award,
    BarChart3,
    RefreshCw,
    Download,
    FileText,
    MapPin
} from 'lucide-react';
import * as echarts from 'echarts';
import * as XLSX from 'xlsx';
import { BASE_URL } from '../../Constants/constants';
import { useAuth } from '../AuthContext/AuthContext';
import { useTranslation } from 'react-i18next';

const ManagementDashboard = ({ activeTab: propActiveTab = 'overview' }) => {
    const { isLoggedIn, userRole, user } = useAuth();
    const { t, i18n } = useTranslation('managementDashboard');
    const [activeTab, setActiveTab] = useState(propActiveTab);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [exporting, setExporting] = useState(false);

    const [dashboardData, setDashboardData] = useState({
        staff: {
            total: 0,
            active: 0,
            inactive: 0,
            byRole: {},
            utilization_rate: 0,
            trend: 0
        },
        checkout: {
            total: 0,
            completed: 0,
            pending: 0,
            completion_rate: 0,
            byDepartment: {},
            trend: 0
        },
        assets: {
            total: 0,
            assigned: 0,
            available: 0,
            maintenance: 0,
            byType: {},
            byCondition: {},
            utilization_rate: 0,
            trend: 0
        },
        training: {
            completed: 0,
            inProgress: 0,
            scheduled: 0,
            byType: {},
            total_enrollments: 0,
            trend: 0
        },
        locations: {
            byDutyStation: {},
            total_stations: 0
        },
        kpis: {
            staff_retention_rate: 0,
            asset_utilization_rate: 0,
            checkout_completion_rate: 0,
            training_completion_rate: 0
        },
        alerts: []
    });

    const [additionalData, setAdditionalData] = useState({
        staffAnalytics: null,
        assetAnalytics: null,
        trainingAnalytics: null
    });

    // Update active tab when prop changes
    useEffect(() => {
        setActiveTab(propActiveTab);
    }, [propActiveTab]);

    // Helper function to get auth token
    const getAuthToken = () => {
        const token = localStorage.getItem('accessToken');
        return token;
    };

    // Excel Export Function
    const exportToExcel = async () => {
        setExporting(true);
        try {
            // Create workbook
            const wb = XLSX.utils.book_new();

            // Overview Sheet
            const overviewData = [
                [t('title'), '', '', ''],
                ['', '', '', ''],
                [t('totalStaff'), dashboardData.staff.total, t('active'), dashboardData.staff.active],
                [t('totalAssets'), dashboardData.assets.total, t('assigned'), dashboardData.assets.assigned],
                [t('activeCheckouts'), dashboardData.checkout.pending, t('completed'), dashboardData.checkout.completed],
                [t('trainingPrograms'), dashboardData.training.total_enrollments, t('completed'), dashboardData.training.completed],
                ['', '', '', ''],
                [t('kpis'), '', '', ''],
                [t('staffRetentionRate'), `${dashboardData.kpis.staff_retention_rate}%`, '', ''],
                [t('assetUtilizationRate'), `${dashboardData.kpis.asset_utilization_rate}%`, '', ''],
                [t('checkoutCompletionRate'), `${dashboardData.kpis.checkout_completion_rate}%`, '', ''],
                [t('trainingCompletionRate'), `${dashboardData.kpis.training_completion_rate}%`, '', '']
            ];

            const overviewWS = XLSX.utils.aoa_to_sheet(overviewData);
            XLSX.utils.book_append_sheet(wb, overviewWS, t('overview'));

            // Staff Data Sheet
            const staffData = [
                [t('staffDistributionByRole'), '', ''],
                [t('role'), t('count'), t('percentage')],
                ...Object.entries(dashboardData.staff.byRole).map(([role, count]) => [
                    role,
                    count,
                    `${((count / dashboardData.staff.total) * 100).toFixed(1)}%`
                ])
            ];

            const staffWS = XLSX.utils.aoa_to_sheet(staffData);
            XLSX.utils.book_append_sheet(wb, staffWS, t('staffAnalytics'));

            // Assets Data Sheet
            const assetsData = [
                [t('assetDistribution'), '', ''],
                [t('type'), t('count'), t('status')],
                ...Object.entries(dashboardData.assets.byType).map(([type, count]) => [type, count, t('active')]),
                ['', '', ''],
                [t('assetCondition'), '', ''],
                [t('condition'), t('count'), ''],
                ...Object.entries(dashboardData.assets.byCondition).map(([condition, count]) => [condition, count, ''])
            ];

            const assetsWS = XLSX.utils.aoa_to_sheet(assetsData);
            XLSX.utils.book_append_sheet(wb, assetsWS, t('assetAnalytics'));

            // Location Data Sheet
            const locationData = [
                [t('staffByDutyStation'), '', ''],
                [t('dutyStation'), t('staffCount'), ''],
                ...Object.entries(dashboardData.locations.byDutyStation).map(([station, count]) => [station, count, ''])
            ];

            const locationWS = XLSX.utils.aoa_to_sheet(locationData);
            XLSX.utils.book_append_sheet(wb, locationWS, t('locationAnalytics'));

            // Training Data Sheet
            const trainingData = [
                [t('trainingPrograms'), '', ''],
                [t('programType'), t('enrollments'), t('status')],
                ...Object.entries(dashboardData.training.byType).map(([type, count]) => [type, count, t('active')]),
                ['', '', ''],
                [t('trainingStatusSummary'), '', ''],
                [t('status'), t('count'), ''],
                [t('completed'), dashboardData.training.completed, ''],
                [t('inProgress'), dashboardData.training.inProgress, ''],
                [t('scheduled'), dashboardData.training.scheduled, '']
            ];

            const trainingWS = XLSX.utils.aoa_to_sheet(trainingData);
            XLSX.utils.book_append_sheet(wb, trainingWS, t('trainingAnalytics'));

            // Alerts Data Sheet
            if (dashboardData.alerts.length > 0) {
                const alertsData = [
                    [t('managementAlerts'), '', ''],
                    [t('priority'), t('message'), t('type')],
                    ...dashboardData.alerts.map(alert => [alert.priority, alert.message, alert.type || t('general')])
                ];

                const alertsWS = XLSX.utils.aoa_to_sheet(alertsData);
                XLSX.utils.book_append_sheet(wb, alertsWS, t('alerts'));
            }

            // Generate filename with current date
            const now = new Date();
            const dateStr = now.toISOString().split('T')[0];
            const filename = `Management_Dashboard_Report_${dateStr}.xlsx`;

            // Write file
            XLSX.writeFile(wb, filename);

        } catch (error) {
            console.error(t('errorExportingToExcel'), error);
            alert(t('exportError'));
        } finally {
            setExporting(false);
        }
    };

    // API call functions with error handling
    const fetchDashboardOverview = async () => {
        const token = getAuthToken();

        if (!token) {
            throw new Error(t('pleaseLogInToAccessTheManagementDashboard'));
        }

        const response = await fetch(`${BASE_URL}/management/dashboard-overview`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            if (response.status === 401) {
                throw new Error(t('yourSessionHasExpiredPleaseLogInAgain'));
            }
            if (response.status === 403) {
                throw new Error(t('youDoNotHavePermissionToAccessThisDashboardManagementLevelAccessRequired'));
            }
            if (response.status === 404) {
                throw new Error(t('dashboardServiceNotAvailablePleaseContactSystemAdministrator'));
            }
            throw new Error(`Service temporarily unavailable (${response.status}). Please try again later.`);
        }

        return await response.json();
    };

    const fetchOperationalKPIs = async () => {
        const token = getAuthToken();

        if (!token) {
            throw new Error(t('pleaseLogInToAccessTheManagementDashboard'));
        }

        const response = await fetch(`${BASE_URL}/management/operational-kpis`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            if (response.status === 401) {
                throw new Error(t('yourSessionHasExpiredPleaseLogInAgain'));
            }
            if (response.status === 403) {
                throw new Error(t('youDoNotHavePermissionToAccessThisDashboardManagementLevelAccessRequired'));
            }
            if (response.status === 404) {
                throw new Error(t('kpiServiceNotAvailablePleaseContactSystemAdministrator'));
            }
            throw new Error(`Service temporarily unavailable (${response.status}). Please try again later.`);
        }

        return await response.json();
    };

    const fetchAlerts = async () => {
        const token = getAuthToken();

        if (!token) {
            throw new Error(t('pleaseLogInToAccessTheManagementDashboard'));
        }

        const response = await fetch(`${BASE_URL}/management/alerts-notifications`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            if (response.status === 401) {
                throw new Error(t('yourSessionHasExpiredPleaseLogInAgain'));
            }
            if (response.status === 403) {
                throw new Error(t('youDoNotHavePermissionToAccessThisDashboardManagementLevelAccessRequired'));
            }
            if (response.status === 404) {
                throw new Error(t('alertServiceNotAvailablePleaseContactSystemAdministrator'));
            }
            throw new Error(`Service temporarily unavailable (${response.status}). Please try again later.`);
        }

        return await response.json();
    };

    // Load initial data
    const loadDashboardData = async () => {
        setLoading(true);
        setError(null);

        try {
            console.log(t('loadingDashboardData'));

            const results = await Promise.allSettled([
                fetchDashboardOverview(),
                fetchOperationalKPIs(),
                fetchAlerts()
            ]);

            const [overviewResult, kpisResult, alertsResult] = results;

            let overviewData = {};
            if (overviewResult.status === 'rejected') {
                console.warn(t('dashboardOverviewFailed'), overviewResult.reason);
                overviewData = {};
            } else {
                overviewData = overviewResult.value || {};
            }

            const kpisData = kpisResult.status === 'fulfilled' ? kpisResult.value : { kpis: {} };
            const alertsData = alertsResult.status === 'fulfilled' ? alertsResult.value : { alerts: [] };

            console.log(t('apiResults'), {
                overviewSuccess: overviewResult.status === 'fulfilled',
                kpisSuccess: kpisResult.status === 'fulfilled',
                alertsSuccess: alertsResult.status === 'fulfilled',
                overviewData,
                kpisData,
                alertsData
            });

            // Transform the API response to match our dashboard structure
            setDashboardData({
                staff: {
                    total: overviewData.staff_metrics?.total_staff || 0,
                    active: overviewData.staff_metrics?.active_staff || 0,
                    inactive: overviewData.staff_metrics?.inactive_staff || 0,
                    byRole: overviewData.staff_metrics?.by_role || {},
                    utilization_rate: overviewData.staff_metrics?.utilization_rate || 0,
                    trend: 0
                },
                checkout: {
                    total: overviewData.checkout_metrics?.total_checkouts || 0,
                    completed: overviewData.checkout_metrics?.completed_checkouts || 0,
                    pending: overviewData.checkout_metrics?.pending_checkouts || 0,
                    completion_rate: overviewData.checkout_metrics?.completion_rate || 0,
                    byDepartment: {
                        'ICT': overviewData.checkout_metrics?.pending_by_department?.ICT || 0,
                        'Security': overviewData.checkout_metrics?.pending_by_department?.Security || 0,
                        'HR': overviewData.checkout_metrics?.pending_by_department?.HR || 0,
                        'Completed': overviewData.checkout_metrics?.completed_checkouts || 0
                    },
                    trend: 0
                },
                assets: {
                    total: overviewData.asset_metrics?.total_assets || 0,
                    assigned: overviewData.asset_metrics?.by_status?.Assigned || 0,
                    available: (overviewData.asset_metrics?.by_status?.Available || 0) +
                        (overviewData.asset_metrics?.by_status?.['In Stock'] || 0),
                    maintenance: (overviewData.asset_metrics?.by_status?.Maintenance || 0) +
                        (overviewData.asset_metrics?.by_status?.Repair || 0),
                    byType: overviewData.asset_metrics?.by_type || {},
                    byCondition: overviewData.asset_metrics?.by_condition || {},
                    utilization_rate: overviewData.asset_metrics?.utilization_rate || 0,
                    trend: 0
                },
                training: {
                    completed: overviewData.training_metrics?.by_status?.Completed ||
                        overviewData.training_metrics?.by_status?.completed ||
                        overviewData.training_metrics?.by_status?.Passed || 0,
                    inProgress: overviewData.training_metrics?.by_status?.[t('inProgress')] ||
                        overviewData.training_metrics?.by_status?.in_progress || 0,
                    scheduled: overviewData.training_metrics?.by_status?.Scheduled ||
                        overviewData.training_metrics?.by_status?.scheduled || 0,
                    byType: overviewData.training_metrics?.by_type || {},
                    total_enrollments: overviewData.training_metrics?.total_enrollments || 0,
                    trend: 0
                },
                locations: {
                    byDutyStation: overviewData.location_metrics?.by_duty_station || {},
                    total_stations: overviewData.location_metrics?.total_stations || 0
                },
                kpis: {
                    staff_retention_rate: kpisData.kpis?.staff_retention_rate || 0,
                    asset_utilization_rate: kpisData.kpis?.asset_utilization_rate || 0,
                    checkout_completion_rate: kpisData.kpis?.checkout_completion_rate || 0,
                    training_completion_rate: kpisData.kpis?.training_completion_rate || 0
                },
                alerts: alertsData.alerts || []
            });

            if (overviewResult.status === 'rejected' && (kpisResult.status === 'fulfilled' || alertsResult.status === 'fulfilled')) {
                console.warn(t('someDashboardServicesAreUnavailableShowingPartialData'));
            }

        } catch (err) {
            console.error(t('errorLoadingDashboardData'), err);
            setError(t('unableToLoadDashboardDataPleaseCheckYourConnectionAndTryAgain'));
        } finally {
            setLoading(false);
        }
    };

    // Wait for auth context to be ready before loading data
    useEffect(() => {
        if (isLoggedIn && getAuthToken()) {
            const timer = setTimeout(() => {
                loadDashboardData();
            }, 200);

            return () => clearTimeout(timer);
        } else if (isLoggedIn && !getAuthToken()) {
            setError(t('authenticationSessionExpiredPleaseLogInAgain'));
            setLoading(false);
        }
    }, [isLoggedIn]);

    // Initialize charts when data changes OR language changes
    useEffect(() => {
        if (!loading) {
            const timer = setTimeout(() => {
                initializeCharts();
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [activeTab, dashboardData, loading, i18n.language, t]); // Added language dependencies

    // Refresh data function
    const refreshData = async () => {
        await loadDashboardData();
        setAdditionalData({
            staffAnalytics: null,
            assetAnalytics: null,
            trainingAnalytics: null
        });
    };

    const initializeCharts = () => {
        try {
            // Check if we have data before initializing charts
            const hasStaffData = Object.keys(dashboardData.staff.byRole).length > 0;
            const hasAssetData = Object.keys(dashboardData.assets.byType).length > 0;
            const hasLocationData = Object.keys(dashboardData.locations.byDutyStation).length > 0;
            const hasTrainingData = Object.keys(dashboardData.training.byType).length > 0;
            const hasCheckoutData = Object.keys(dashboardData.checkout.byDepartment).length > 0;

            // Dispose existing charts to prevent memory leaks and ensure fresh rendering
            const chartElements = [
                'staff-distribution',
                'asset-utilization',
                'training-progress',
                'location-distribution',
                'checkout-timeline'
            ];

            chartElements.forEach(id => {
                const element = document.getElementById(id);
                if (element) {
                    const existingChart = echarts.getInstanceByDom(element);
                    if (existingChart) {
                        existingChart.dispose();
                    }
                }
            });

            // Staff Distribution Pie Chart
            const staffElement = document.getElementById('staff-distribution');
            if (staffElement) {
                const staffChart = echarts.init(staffElement);

                if (!hasStaffData) {
                    const noDataOption = {
                        title: {
                            text: t('noDataAvailable'),
                            left: 'center',
                            top: 'middle',
                            textStyle: { fontSize: 16, color: '#9ca3af' }
                        }
                    };
                    staffChart.setOption(noDataOption);
                } else {
                    const staffOption = {
                        title: {
                            text: t('staffDistributionByRole'),
                            left: 'center',
                            textStyle: { fontSize: 16, fontWeight: 'bold' }
                        },
                        tooltip: {
                            trigger: 'item',
                            formatter: '{a} <br/>{b}: {c} ({d}%)'
                        },
                        legend: {
                            orient: 'horizontal',
                            bottom: '5%',
                            data: Object.keys(dashboardData.staff.byRole)
                        },
                        series: [{
                            name: t('staff'),
                            type: 'pie',
                            radius: ['40%', '70%'],
                            avoidLabelOverlap: false,
                            itemStyle: {
                                borderRadius: 10,
                                borderColor: '#fff',
                                borderWidth: 2
                            },
                            label: {
                                show: false,
                                position: 'center'
                            },
                            emphasis: {
                                label: {
                                    show: true,
                                    fontSize: '18',
                                    fontWeight: 'bold'
                                }
                            },
                            labelLine: {
                                show: false
                            },
                            data: Object.entries(dashboardData.staff.byRole).map(([key, value]) => ({
                                value,
                                name: key
                            }))
                        }]
                    };
                    staffChart.setOption(staffOption);
                }
            }

            // Asset Utilization Bar Chart
            const assetElement = document.getElementById('asset-utilization');
            if (assetElement) {
                const assetChart = echarts.init(assetElement);

                if (!hasAssetData) {
                    const noDataOption = {
                        title: {
                            text: t('noDataAvailable'),
                            left: 'center',
                            top: 'middle',
                            textStyle: { fontSize: 16, color: '#9ca3af' }
                        }
                    };
                    assetChart.setOption(noDataOption);
                } else {
                    const assetOption = {
                        title: {
                            text: t('assetDistributionByType'),
                            left: 'center',
                            textStyle: { fontSize: 16, fontWeight: 'bold' }
                        },
                        tooltip: {
                            trigger: 'axis',
                            axisPointer: {
                                type: 'shadow'
                            }
                        },
                        xAxis: {
                            type: 'category',
                            data: Object.keys(dashboardData.assets.byType),
                            axisLabel: {
                                rotate: 45
                            }
                        },
                        yAxis: {
                            type: 'value'
                        },
                        series: [{
                            data: Object.values(dashboardData.assets.byType),
                            type: 'bar',
                            itemStyle: {
                                borderRadius: [4, 4, 0, 0]
                            }
                        }]
                    };
                    assetChart.setOption(assetOption);
                }
            }

            // Training Progress Chart
            const trainingElement = document.getElementById('training-progress');
            if (trainingElement) {
                const trainingChart = echarts.init(trainingElement);

                if (!hasTrainingData) {
                    const noDataOption = {
                        title: {
                            text: t('noDataAvailable'),
                            left: 'center',
                            top: 'middle',
                            textStyle: { fontSize: 16, color: '#9ca3af' }
                        }
                    };
                    trainingChart.setOption(noDataOption);
                } else {
                    const trainingOption = {
                        title: {
                            text: t('trainingProgramsOverview'),
                            left: 'center',
                            textStyle: { fontSize: 16, fontWeight: 'bold' }
                        },
                        tooltip: {
                            trigger: 'item'
                        },
                        legend: {
                            orient: 'horizontal',
                            bottom: '5%'
                        },
                        series: [{
                            name: t('training'),
                            type: 'pie',
                            radius: '70%',
                            data: Object.entries(dashboardData.training.byType).map(([key, value]) => ({
                                value,
                                name: key
                            })),
                            emphasis: {
                                itemStyle: {
                                    shadowBlur: 10,
                                    shadowOffsetX: 0,
                                    shadowColor: 'rgba(0, 0, 0, 0.5)'
                                }
                            }
                        }]
                    };
                    trainingChart.setOption(trainingOption);
                }
            }

            // Location Distribution
            const locationElement = document.getElementById('location-distribution');
            if (locationElement) {
                const locationChart = echarts.init(locationElement);

                if (!hasLocationData) {
                    const noDataOption = {
                        title: {
                            text: t('noDataAvailable'),
                            left: 'center',
                            top: 'middle',
                            textStyle: { fontSize: 16, color: '#9ca3af' }
                        }
                    };
                    locationChart.setOption(noDataOption);
                } else {
                    const locationOption = {
                        title: {
                            text: t('staffByDutyStation'),
                            left: 'center',
                            textStyle: { fontSize: 16, fontWeight: 'bold' }
                        },
                        tooltip: {
                            trigger: 'axis',
                            axisPointer: {
                                type: 'shadow'
                            }
                        },
                        xAxis: {
                            type: 'category',
                            data: Object.keys(dashboardData.locations.byDutyStation),
                            axisLabel: {
                                rotate: 30
                            }
                        },
                        yAxis: {
                            type: 'value'
                        },
                        series: [{
                            data: Object.values(dashboardData.locations.byDutyStation),
                            type: 'bar',
                            itemStyle: {
                                color: '#06b6d4',
                                borderRadius: [4, 4, 0, 0]
                            }
                        }]
                    };
                    locationChart.setOption(locationOption);
                }
            }

            // Checkout Status Timeline
            const checkoutElement = document.getElementById('checkout-timeline');
            if (checkoutElement) {
                const checkoutChart = echarts.init(checkoutElement);

                if (!hasCheckoutData) {
                    const noDataOption = {
                        title: {
                            text: t('noDataAvailable'),
                            left: 'center',
                            top: 'middle',
                            textStyle: { fontSize: 16, color: '#9ca3af' }
                        }
                    };
                    checkoutChart.setOption(noDataOption);
                } else {
                    const checkoutOption = {
                        title: {
                            text: t('checkoutProcessStatus'),
                            left: 'center',
                            textStyle: { fontSize: 16, fontWeight: 'bold' }
                        },
                        tooltip: {
                            trigger: 'item',
                            formatter: '{a} <br/>{b}: {c}'
                        },
                        series: [{
                            name: t('checkout'),
                            type: 'pie',
                            radius: ['50%', '70%'],
                            avoidLabelOverlap: false,
                            data: Object.entries(dashboardData.checkout.byDepartment).map(([key, value]) => ({
                                value,
                                name: key
                            })),
                            emphasis: {
                                itemStyle: {
                                    shadowBlur: 10,
                                    shadowOffsetX: 0,
                                    shadowColor: 'rgba(0, 0, 0, 0.5)'
                                }
                            }
                        }]
                    };
                    checkoutChart.setOption(checkoutOption);
                }
            }
        } catch (error) {
            console.error(t('errorInitializingCharts'), error);
        }
    };

    // Bootstrap StatCard component
    const StatCard = ({ icon: Icon, title, value, variant = "primary", subtext, isLoading = false, trend }) => (
        <Card className="h-100 shadow-sm">
            <Card.Body className="d-flex align-items-center">
                <div className={`p-3 rounded-circle bg-${variant} bg-opacity-10 me-4`}>
                    <Icon size={28} className={`text-${variant}`} />
                </div>
                <div className="flex-grow-1">
                    <Card.Title className="h6 text-muted mb-1 text-uppercase">{title}</Card.Title>
                    {isLoading ? (
                        <div>
                            <Spinner animation="border" size="sm" />
                            <span className="ms-2">{t('loading')}</span>
                        </div>
                    ) : (
                        <>
                            <h3 className="mb-1 fw-bold">{value}</h3>
                            {subtext && <small className="text-muted">{subtext}</small>}
                            {trend !== 0 && (
                                <div className="mt-1">
                                    <Badge bg={trend > 0 ? 'success' : trend < 0 ? 'danger' : 'secondary'}>
                                        {trend > 0 ? '+' : ''}{trend}%
                                    </Badge>
                                    <small className="text-muted ms-1">{t('vsLastMonth')}</small>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </Card.Body>
        </Card>
    );

    // Bootstrap MetricCard component
    const MetricCard = ({ title, children, isLoading = false, isEmpty = false, className = "" }) => (
        <Card className={`shadow-sm h-100 ${className}`}>
            <Card.Header className="bg-light">
                <Card.Title className="h6 mb-0">{title}</Card.Title>
            </Card.Header>
            <Card.Body>
                {isLoading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{height: '300px'}}>
                        <div className="text-center">
                            <Spinner animation="border" />
                            <div className="mt-2">{t('loading')}</div>
                        </div>
                    </div>
                ) : isEmpty ? (
                    <div className="d-flex flex-column justify-content-center align-items-center text-muted" style={{height: '300px'}}>
                        <BarChart3 size={48} className="mb-3 text-secondary" />
                        <h6>{t('noDataAvailable')}</h6>
                        <p className="text-center">{t('dataWillAppear')}</p>
                    </div>
                ) : (
                    children
                )}
            </Card.Body>
        </Card>
    );

    // Loading state
    if (loading) {
        return (
            <Container fluid className="py-4 bg-light min-vh-100">
                <Container>
                    <div className="text-center">
                        <Spinner animation="border" size="lg" />
                        <h4 className="mt-3">{t('loadingDashboard')}</h4>
                        <p className="text-muted">{t('pleaseWait')}</p>
                    </div>
                </Container>
            </Container>
        );
    }

    // Error state
    if (error) {
        return (
            <Container fluid className="py-4 bg-light min-vh-100">
                <Container>
                    <Alert variant="danger" className="d-flex align-items-center">
                        <AlertTriangle className="me-3" size={24} />
                        <div className="flex-grow-1">
                            <Alert.Heading>{t('errorLoadingDashboard')}</Alert.Heading>
                            <p className="mb-2">{error}</p>
                            <div className="d-flex gap-2">
                                <Button variant="outline-danger" size="sm" onClick={refreshData}>
                                    <RefreshCw size={16} className="me-1" />
                                    {t('retry')}
                                </Button>
                                {error.includes('log in') && (
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={() => window.location.href = '/login'}
                                    >
                                        {t('goToLogin')}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </Alert>
                </Container>
            </Container>
        );
    }

    const renderOverview = () => {
        const hasStaffData = Object.keys(dashboardData.staff.byRole).length > 0;
        const hasAssetData = Object.keys(dashboardData.assets.byType).length > 0;
        const hasCheckoutData = Object.keys(dashboardData.checkout.byDepartment).length > 0;
        const hasTrainingData = Object.keys(dashboardData.training.byType).length > 0;

        return (
            <>
                {/* KPI Cards */}
                <Row className="g-4 mb-4">
                    <Col lg={3} md={6}>
                        <StatCard
                            icon={Users}
                            title={t('totalStaff')}
                            value={dashboardData.staff.total}
                            variant="primary"
                            subtext={`${dashboardData.staff.active} ${t('active')}`}
                            trend={dashboardData.staff.trend}
                            isLoading={loading}
                        />
                    </Col>
                    <Col lg={3} md={6}>
                        <StatCard
                            icon={Laptop}
                            title={t('totalAssets')}
                            value={dashboardData.assets.total}
                            variant="success"
                            subtext={`${dashboardData.assets.assigned} ${t('assigned')}`}
                            trend={dashboardData.assets.trend}
                            isLoading={loading}
                        />
                    </Col>
                    <Col lg={3} md={6}>
                        <StatCard
                            icon={UserCheck}
                            title={t('activeCheckouts')}
                            value={dashboardData.checkout.pending}
                            variant="warning"
                            subtext={`${dashboardData.checkout.completed} ${t('completed')}`}
                            trend={dashboardData.checkout.trend}
                            isLoading={loading}
                        />
                    </Col>
                    <Col lg={3} md={6}>
                        <StatCard
                            icon={Award}
                            title={t('trainingPrograms')}
                            value={dashboardData.training.total_enrollments}
                            variant="info"
                            subtext={t('totalEnrollments')}
                            trend={dashboardData.training.trend}
                            isLoading={loading}
                        />
                    </Col>
                </Row>

                {/* Alerts Section */}
                {!loading && dashboardData.alerts.length > 0 && (
                    <Row className="mb-4">
                        <Col>
                            <Alert variant="warning" className="border-0 shadow-sm">
                                <Alert.Heading className="h6 d-flex align-items-center">
                                    <AlertTriangle size={20} className="me-2" />
                                    {t('managementAlerts')} ({dashboardData.alerts.length})
                                </Alert.Heading>
                                <div className="mt-3">
                                    {dashboardData.alerts.slice(0, 3).map((alert, index) => (
                                        <div key={index} className="d-flex align-items-start mb-2">
                                            <Badge
                                                bg={alert.priority === 'high' ? 'danger' : alert.priority === 'medium' ? 'warning' : 'info'}
                                                className="me-2 mt-1"
                                            >
                                                {alert.priority}
                                            </Badge>
                                            <span className="small">{alert.message}</span>
                                        </div>
                                    ))}
                                    {dashboardData.alerts.length > 3 && (
                                        <small className="text-muted">
                                            +{dashboardData.alerts.length - 3} more alerts
                                        </small>
                                    )}
                                </div>
                            </Alert>
                        </Col>
                    </Row>
                )}

                {/* Charts Grid */}
                <Row className="g-4 mb-4">
                    <Col lg={6}>
                        <MetricCard
                            title={t('staffDistributionByRole')}
                            isLoading={loading}
                            isEmpty={!loading && !hasStaffData}
                        >
                            <div id="staff-distribution" style={{ width: '100%', height: '300px' }}></div>
                        </MetricCard>
                    </Col>
                    <Col lg={6}>
                        <MetricCard
                            title={t('assetDistributionByType')}
                            isLoading={loading}
                            isEmpty={!loading && !hasAssetData}
                        >
                            <div id="asset-utilization" style={{ width: '100%', height: '300px' }}></div>
                        </MetricCard>
                    </Col>
                </Row>

                <Row className="g-4">
                    <Col lg={6}>
                        <MetricCard
                            title={t('checkoutProcessStatus')}
                            isLoading={loading}
                            isEmpty={!loading && !hasCheckoutData}
                        >
                            <div id="checkout-timeline" style={{ width: '100%', height: '300px' }}></div>
                        </MetricCard>
                    </Col>
                    <Col lg={6}>
                        <MetricCard
                            title={t('trainingProgramsOverview')}
                            isLoading={loading}
                            isEmpty={!loading && !hasTrainingData}
                        >
                            <div id="training-progress" style={{ width: '100%', height: '300px' }}></div>
                        </MetricCard>
                    </Col>
                </Row>
            </>
        );
    };

    const renderStaffAnalytics = () => {
        const hasLocationData = Object.keys(dashboardData.locations.byDutyStation).length > 0;
        const hasStaffRoleData = Object.keys(dashboardData.staff.byRole).length > 0;

        return (
            <>
                {/* Staff KPIs */}
                <Row className="g-4 mb-4">
                    <Col lg={4}>
                        <StatCard
                            icon={Users}
                            title={t('activeStaff')}
                            value={dashboardData.staff.active}
                            variant="success"
                            subtext={t('currentlyEmployed')}
                            trend={dashboardData.staff.trend}
                        />
                    </Col>
                    <Col lg={4}>
                        <StatCard
                            icon={TrendingUp}
                            title={t('staffRetention')}
                            value={`${dashboardData.kpis.staff_retention_rate}%`}
                            variant="primary"
                            subtext={t('retentionRate')}
                        />
                    </Col>
                    <Col lg={4}>
                        <StatCard
                            icon={MapPin}
                            title={t('dutyStations')}
                            value={dashboardData.locations.total_stations}
                            variant="info"
                            subtext={t('acrossLocations')}
                        />
                    </Col>
                </Row>

                {/* Staff Charts */}
                <Row className="g-4">
                    <Col lg={8}>
                        <MetricCard
                            title={t('staffByDutyStation')}
                            isLoading={loading}
                            isEmpty={!loading && !hasLocationData}
                        >
                            <div id="location-distribution" style={{ width: '100%', height: '350px' }}></div>
                        </MetricCard>
                    </Col>
                    <Col lg={4}>
                        <Card className="shadow-sm h-100">
                            <Card.Header className="bg-light">
                                <Card.Title className="h6 mb-0">{t('staffOverview')}</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                {hasStaffRoleData ? (
                                    <div className="d-grid gap-3">
                                        <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                                            <span className="fw-medium">{t('totalStaff')}</span>
                                            <Badge bg="primary" className="fs-6">
                                                {dashboardData.staff.total}
                                            </Badge>
                                        </div>
                                        {Object.entries(dashboardData.staff.byRole).map(([role, count]) => (
                                            <div key={role} className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                                                <span className="fw-medium">{role} {t('department')}</span>
                                                <Badge
                                                    bg={
                                                        role === 'ICT' ? 'info' :
                                                            role === t('security') ? 'danger' :
                                                                role === 'HR' ? 'success' : 'secondary'
                                                    }
                                                    className="fs-6"
                                                >
                                                    {count}
                                                </Badge>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center text-muted py-5">
                                        <Users size={48} className="mb-3" />
                                        <p>{t('noStaffRoleDataAvailable')}</p>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </>
        );
    };

    const renderAssetAnalytics = () => {
        const hasAssetTypeData = Object.keys(dashboardData.assets.byType).length > 0;
        const hasAssetConditionData = Object.keys(dashboardData.assets.byCondition).length > 0;

        return (
            <>
                {/* Asset KPIs */}
                <Row className="g-4 mb-4">
                    <Col lg={3} md={6}>
                        <StatCard
                            icon={Monitor}
                            title={t('totalAssets')}
                            value={dashboardData.assets.total}
                            variant="primary"
                            subtext={t('allEquipment')}
                            trend={dashboardData.assets.trend}
                        />
                    </Col>
                    <Col lg={3} md={6}>
                        <StatCard
                            icon={CheckCircle}
                            title={t('assigned')}
                            value={dashboardData.assets.assigned}
                            variant="success"
                            subtext={t('currentlyInUse')}
                        />
                    </Col>
                    <Col lg={3} md={6}>
                        <StatCard
                            icon={Clock}
                            title={t('available')}
                            value={dashboardData.assets.available}
                            variant="info"
                            subtext={t('readyForAssignment')}
                        />
                    </Col>
                    <Col lg={3} md={6}>
                        <StatCard
                            icon={AlertTriangle}
                            title={t('maintenance')}
                            value={dashboardData.assets.maintenance}
                            variant="warning"
                            subtext={t('needsAttention')}
                        />
                    </Col>
                </Row>

                {/* Asset Charts */}
                <Row className="g-4">
                    <Col lg={6}>
                        <Card className="shadow-sm h-100">
                            <Card.Header className="bg-light">
                                <Card.Title className="h6 mb-0">{t('assetConditionAnalysis')}</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                {hasAssetConditionData ? (
                                    <div className="d-grid gap-3">
                                        {Object.entries(dashboardData.assets.byCondition).map(([condition, count]) => (
                                            <div key={condition} className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                                                <div className="d-flex align-items-center">
                                                    <div
                                                        className={`rounded-circle me-3`}
                                                        style={{
                                                            width: '12px',
                                                            height: '12px',
                                                            backgroundColor:
                                                                condition === t('excellent') ? '#28a745' :
                                                                    condition === 'Good' ? '#007bff' :
                                                                        condition === 'Fair' ? '#ffc107' : '#dc3545'
                                                        }}
                                                    ></div>
                                                    <span className="fw-medium">{condition}</span>
                                                </div>
                                                <Badge bg="secondary" className="fs-6">{count}</Badge>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center text-muted py-5">
                                        <Monitor size={48} className="mb-3" />
                                        <p>{t('noAssetConditionDataAvailable')}</p>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col lg={6}>
                        <Card className="shadow-sm h-100">
                            <Card.Header className="bg-light">
                                <Card.Title className="h6 mb-0">{t('assetUtilization')}</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <div className="mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span className="fw-medium">{t('utilizationRate')}</span>
                                        <span className="h5 mb-0 text-primary fw-bold">
                                            {(dashboardData.assets.utilization_rate).toFixed(1)}%
                                        </span>
                                    </div>
                                    <div className="progress" style={{height: '8px'}}>
                                        <div
                                            className="progress-bar bg-gradient"
                                            style={{ width: `${dashboardData.assets.utilization_rate}%` }}
                                        ></div>
                                    </div>
                                </div>

                                <hr />

                                <div>
                                    <h6 className="fw-bold mb-3">{t('assetDistribution')}</h6>
                                    <div className="d-grid gap-2">
                                        {Object.entries(dashboardData.assets.byType).length > 0 ? (
                                            Object.entries(dashboardData.assets.byType).map(([type, count]) => (
                                                <div key={type} className="d-flex justify-content-between align-items-center p-2 bg-light rounded">
                                                    <span>{type}</span>
                                                    <Badge bg="primary">{count}</Badge>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="text-center text-muted py-4">
                                                <small>{t('noAssetTypesAvailable')}</small>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </>
        );
    };

    return (
        <Container fluid className="py-4 bg-light min-vh-100">
            <Container>
                {/* Header */}
                <Row className="mb-4">
                    <Col>
                        <div className="d-flex justify-content-between align-items-start">
                            <div>
                                <h1 className="display-5 fw-bold mb-2">{t('title')}</h1>
                                <p className="text-muted mb-3">{t('comprehensiveInsights')}</p>
                            </div>
                            <div className="d-flex gap-2">
                                <Button
                                    variant="outline-secondary"
                                    onClick={refreshData}
                                    disabled={loading}
                                    className="d-flex align-items-center"
                                >
                                    <RefreshCw size={16} className={`me-2 ${loading ? 'spinning' : ''}`} />
                                    {t('refresh')}
                                </Button>
                                <Button
                                    variant="success"
                                    className="d-flex align-items-center"
                                    onClick={exportToExcel}
                                    disabled={exporting}
                                >
                                    <Download size={16} className="me-2" />
                                    {exporting ? t('exporting') : t('exportToExcel')}
                                </Button>
                                <Button variant="primary" className="d-flex align-items-center">
                                    <FileText size={16} className="me-2" />
                                    {t('exportReport')}
                                </Button>
                            </div>
                        </div>
                    </Col>
                </Row>

                {/* Navigation Tabs */}
                <Row className="mb-4">
                    <Col>
                        <Card className="shadow-sm">
                            <Card.Body className="p-2">
                                <Nav variant="pills" activeKey={activeTab} className="justify-content-center">
                                    <Nav.Item>
                                        <Nav.Link
                                            eventKey="overview"
                                            onClick={() => setActiveTab('overview')}
                                            className="d-flex align-items-center px-4"
                                        >
                                            <BarChart3 size={18} className="me-2" />
                                            {t('overview')}
                                        </Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link
                                            eventKey="staff"
                                            onClick={() => setActiveTab('staff')}
                                            className="d-flex align-items-center px-4"
                                        >
                                            <Users size={18} className="me-2" />
                                            {t('staffAnalytics')}
                                        </Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link
                                            eventKey="assets"
                                            onClick={() => setActiveTab('assets')}
                                            className="d-flex align-items-center px-4"
                                        >
                                            <Monitor size={18} className="me-2" />
                                            {t('assetAnalytics')}
                                        </Nav.Link>
                                    </Nav.Item>
                                </Nav>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Content */}
                {activeTab === 'overview' && renderOverview()}
                {activeTab === 'staff' && renderStaffAnalytics()}
                {activeTab === 'assets' && renderAssetAnalytics()}
            </Container>
        </Container>
    );
};

export default ManagementDashboard;